import React, { useEffect, useId, useRef } from 'react';
import {
  FaCheck,
  FaClock,
  FaHome,
  FaTimes,
  FaUtensils,
} from 'react-icons/fa';
import {
  formatOrderAmountInr,
  formatOrderPlacedAt,
  formatStatusLabel,
  getOrderStatusDisplayLabel,
  getOrderBillRows,
  getOrderCardBrandName,
  getOrderCardLocation,
  getOrderDeliveryLines,
  getOrderDeliveryTitle,
  getOrderDetailLineItems,
  getOrderItemsCommaList,
  getOrderMapDestQueryText,
  getOrderMapOriginFallbackText,
  getOrderPaymentMethod,
  getOrderRazorpayPaymentId,
  getOrderSpecialInstructions,
  getOrderStatusString,
  getOrderTrackingMapPoints,
  getPastCardSubtitle,
  getStatusVisualKind,
  orderIsPast,
  shouldShowCancelPendingOrder,
  sumOrderLineTotals,
} from './accountOrdersUtils';

export const ACCOUNT_ORDERS_DETAIL_PANE_ID = 'account-orders-detail-pane';

/**
 * @param {{
 *   pickup: { lat: number; lng: number } | null;
 *   drop: { lat: number; lng: number } | null;
 *   originText: string;
 *   destText: string;
 *   showTitle?: boolean;
 * }} props
 */
function OrderTrackingMapEmbed({ pickup, drop, originText, destText, showTitle = true }) {
  const enc = (s) => encodeURIComponent(s);
  const mapViewSrc = (lat, lng, zoom = 13) =>
    `https://www.google.com/maps?q=${lat},${lng}&z=${zoom}&output=embed`;
  const directionsSrc = (saddr, daddr) =>
    `https://maps.google.com/maps?saddr=${saddr}&daddr=${daddr}&f=d&output=embed`;

  let src = '';
  if (pickup && drop) {
    src = directionsSrc(`${pickup.lat},${pickup.lng}`, `${drop.lat},${drop.lng}`);
  } else if (pickup && destText && destText.length > 6) {
    src = directionsSrc(`${pickup.lat},${pickup.lng}`, enc(destText));
  } else if (drop && originText && originText.length > 6) {
    src = directionsSrc(enc(originText), `${drop.lat},${drop.lng}`);
  } else if (drop) {
    src = mapViewSrc(drop.lat, drop.lng, 14);
  } else if (pickup) {
    src = mapViewSrc(pickup.lat, pickup.lng, 14);
  } else if (originText && destText && destText.length > 6) {
    src = directionsSrc(enc(originText), enc(destText));
  } else if (destText && destText.length > 6) {
    src = `https://maps.google.com/maps?q=${enc(destText)}&z=14&output=embed`;
  }

  if (!src) return null;

  let href = '';
  if (pickup && drop) {
    href = `https://www.google.com/maps/dir/?api=1&origin=${pickup.lat},${pickup.lng}&destination=${drop.lat},${drop.lng}`;
  } else if (pickup && destText && destText.length > 6) {
    href = `https://www.google.com/maps/dir/?api=1&origin=${pickup.lat},${pickup.lng}&destination=${enc(destText)}`;
  } else if (drop && originText && originText.length > 6) {
    href = `https://www.google.com/maps/dir/?api=1&origin=${enc(originText)}&destination=${drop.lat},${drop.lng}`;
  } else if (drop) {
    href = `https://www.google.com/maps/search/?api=1&query=${drop.lat},${drop.lng}`;
  } else if (pickup) {
    href = `https://www.google.com/maps/search/?api=1&query=${pickup.lat},${pickup.lng}`;
  } else if (originText && destText && destText.length > 6) {
    href = `https://www.google.com/maps/dir/?api=1&origin=${enc(originText)}&destination=${enc(destText)}`;
  } else if (destText && destText.length > 6) {
    href = `https://www.google.com/maps/search/?api=1&query=${enc(destText)}`;
  }

  return (
    <div className="account-orders-drawer__map-section">
      {showTitle ? <h3 className="account-orders-drawer__section-title">Delivery Route</h3> : null}
      <div className="account-orders-drawer__map-frame-wrap">
        <iframe
          title="Order route map"
          className="account-orders-drawer__map-frame"
          src={src}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          tabIndex={-1}
        />
        {href ? (
          <a
            className="account-orders-drawer__map-hit"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open route in Google Maps"
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * @param {{
 *   order: {
 *     displayId: string;
 *     status: string;
 *     orderType: string;
 *     total: number | null;
 *     placedAt: Date | null;
 *     raw: Record<string, unknown>;
 *   };
 *   isOpen: boolean;
 *   onClose: () => void;
 *   onCancelOrder?: (order: typeof order) => void;
 *   cancelling?: boolean;
 * }} props
 */
export default function OrderDetailsSidePane({
  order,
  isOpen,
  onClose,
  onCancelOrder,
  cancelling = false,
}) {
  const closeRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      closeRef.current?.focus();
    }, 0);
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !order) return null;

  const status = getOrderStatusString(order);
  const brand = getOrderCardBrandName(order.raw);
  const location = getOrderCardLocation(order.raw);
  const deliveryTitle = getOrderDeliveryTitle(order.raw);
  const deliveryLines = getOrderDeliveryLines(order.raw);
  const vk = getStatusVisualKind(status);
  const isLiveLifecycle = !orderIsPast(order);
  const statusMain = getOrderStatusDisplayLabel(status);

  const placedLine = order.placedAt
    ? `Placed ${formatOrderPlacedAt(order.placedAt)}`
    : '';

  const lineItems = getOrderDetailLineItems(order.raw);
  const itemsFallback =
    getOrderItemsCommaList(order.raw) || getPastCardSubtitle(order.raw) || '';
  const itemCount = lineItems.length || (itemsFallback ? 1 : 0);
  const specialInstructions = getOrderSpecialInstructions(order.raw);

  const sumLines = sumOrderLineTotals(lineItems);
  let { rows: billRows, grandTotal } = getOrderBillRows(order.raw, order.total);
  const hasSubTotalRow = billRows.some((r) => /sub total|item total/i.test(r.label));
  if (!hasSubTotalRow && sumLines != null) {
    billRows = [{ label: 'Sub total', amount: sumLines }, ...billRows];
  }

  const paymentMethod = getOrderPaymentMethod(order.raw);
  const razorpayPaymentId = getOrderRazorpayPaymentId(order.raw);
  const totalLabel = formatOrderAmountInr(grandTotal ?? order.total) || '—';
  const orderTypeLabel = formatStatusLabel(order.orderType || '');

  const mapPoints = getOrderTrackingMapPoints(order.raw);
  const destQuery = getOrderMapDestQueryText(order.raw);
  const originQuery = getOrderMapOriginFallbackText(order.raw);
  const showLiveMap =
    isLiveLifecycle &&
    !!(mapPoints.pickup || mapPoints.drop || (destQuery && destQuery.length > 6));
  const showPriceSection = billRows.length > 0 || grandTotal != null || order.total != null;
  const showCancel = Boolean(onCancelOrder) && shouldShowCancelPendingOrder(status);

  const pickupSubtitle =
    /pickup/i.test(String(order.orderType || '')) || /pickup/i.test(location || '')
      ? 'Pickup'
      : location || 'Pickup';

  return (
    <>
      <div className="account-orders-drawer" data-open>
        <button
          type="button"
          className="account-orders-drawer__backdrop"
          aria-label="Close order details"
          onClick={onClose}
        />
        <aside
          id={ACCOUNT_ORDERS_DETAIL_PANE_ID}
          className="account-orders-drawer__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <header className="account-orders-drawer__header">
            <button
              ref={closeRef}
              type="button"
              className="account-orders-drawer__close"
              aria-label="Close"
              onClick={onClose}
            >
              <FaTimes aria-hidden />
            </button>
            <h2 id={titleId} className="account-orders-drawer__title">
              Order #{order.displayId}
            </h2>
          </header>

          <div className="account-orders-drawer__scroll">
            <div
              className={`account-orders-drawer__status account-orders-drawer__status--${vk}`}
              role="status"
            >
              <span className="account-orders-drawer__status-dot" aria-hidden>
                {vk === 'delivered' ? <FaCheck /> : vk === 'cancelled' ? <FaTimes /> : <FaClock />}
              </span>
              <div className="account-orders-drawer__status-text">
                <p className="account-orders-drawer__status-main">{statusMain}</p>
                {placedLine ? <p className="account-orders-drawer__status-meta">{placedLine}</p> : null}
              </div>
            </div>

            {showLiveMap ? (
              <div className="account-orders-drawer__tracking-block">
                <OrderTrackingMapEmbed
                  pickup={mapPoints.pickup}
                  drop={mapPoints.drop}
                  originText={originQuery}
                  destText={destQuery}
                />
                {/* Live tracking link: use account-orders-drawer__map-link (right-aligned) when driver GPS is wired up. */}
              </div>
            ) : null}

            <div className="account-orders-drawer__route">
              <div className="account-orders-drawer__route-row">
                <span
                  className="account-orders-drawer__route-icon account-orders-drawer__route-icon--store"
                  aria-hidden
                >
                  <FaUtensils />
                </span>
                <div className="account-orders-drawer__route-text">
                  <div className="account-orders-drawer__route-line account-orders-drawer__route-line--strong">
                    {brand || 'DigiRasa'}
                  </div>
                  <div className="account-orders-drawer__route-line--muted">{pickupSubtitle}</div>
                </div>
              </div>
              <div className="account-orders-drawer__route-connector" aria-hidden />
              <div className="account-orders-drawer__route-row">
                <span className="account-orders-drawer__route-icon account-orders-drawer__route-icon--home" aria-hidden>
                  <FaHome />
                </span>
                <div className="account-orders-drawer__route-text">
                  <div className="account-orders-drawer__route-line account-orders-drawer__route-line--strong">
                    {deliveryTitle || 'Delivery'}
                  </div>
                  {deliveryLines.length ? (
                    deliveryLines.map((line, idx) => (
                      <div key={`${idx}-${line}`} className="account-orders-drawer__route-line--muted">
                        {line}
                      </div>
                    ))
                  ) : (
                    <div className="account-orders-drawer__route-line--muted">
                      Address not available for this order.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <hr className="account-orders-drawer__rule account-orders-drawer__rule--dotted" />

            <section className="account-orders-drawer__items-section" aria-label="Order items">
              <p className="account-orders-drawer__items-kicker">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </p>
              {lineItems.length ? (
                <ul className="account-orders-drawer__item-list">
                  {lineItems.map((row, idx) => (
                    <li key={`${idx}-${row.name}-${row.qty}`} className="account-orders-drawer__item-row">
                      <span
                        className={`account-orders-drawer__veg${row.isVeg === true ? ' account-orders-drawer__veg--yes' : ''}${row.isVeg === false ? ' account-orders-drawer__veg--no' : ''}`}
                        title={
                          row.isVeg === true
                            ? 'Vegetarian'
                            : row.isVeg === false
                              ? 'Non-vegetarian'
                              : 'Diet type unknown'
                        }
                        aria-hidden
                      />
                      <div className="account-orders-drawer__item-main">
                        <div className="account-orders-drawer__item-name">
                          {row.name}
                          {row.qty > 1 ? ` × ${row.qty}` : null}
                        </div>
                        {row.subtitle ? (
                          <div className="account-orders-drawer__item-sub">{row.subtitle}</div>
                        ) : null}
                        {row.addOns?.length ? (
                          <ul className="account-orders-drawer__item-addons">
                            {row.addOns.map((addon) => (
                              <li key={addon.id ?? addon.name}>
                                + {addon.name}
                                <span className="account-orders-drawer__item-addon-price">
                                  (+₹{Number(addon.price || 0).toFixed(2)})
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                      <div className="account-orders-drawer__item-price">
                        {row.lineTotal != null ? formatOrderAmountInr(row.lineTotal) : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : itemsFallback ? (
                <p className="account-orders-drawer__items-fallback">{itemsFallback}</p>
              ) : (
                <p className="account-orders-drawer__items-fallback account-orders-drawer__items-fallback--muted">
                  Item details are not available for this order.
                </p>
              )}
            </section>

            {specialInstructions ? (
              <section className="account-orders-drawer__notes" aria-label="Special instructions">
                <h3 className="account-orders-drawer__notes-label">Special instructions</h3>
                <p className="account-orders-drawer__notes-text">{specialInstructions}</p>
              </section>
            ) : null}

            {showPriceSection ? (
              <section className="account-orders-drawer__bill-section" aria-label="Price breakdown">
                {billRows.length > 0 ? (
                  <div className="account-orders-drawer__bill">
                    {billRows.map((row) => (
                      <div key={row.label} className="account-orders-drawer__bill-row">
                        <span className="account-orders-drawer__bill-label">
                          {row.label}
                          {row.note ? (
                            <span className="account-orders-drawer__bill-note"> ({row.note})</span>
                          ) : null}
                        </span>
                        <span className="account-orders-drawer__bill-amt">
                          {row.amount != null ? formatOrderAmountInr(row.amount) : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="account-orders-drawer__bill-empty">
                    Detailed charges are not available for this order. The total below is from your order summary.
                  </p>
                )}

                <hr className="account-orders-drawer__rule account-orders-drawer__rule--dotted" />

                <div className="account-orders-drawer__bill-row">
                  <span className="account-orders-drawer__item-name">Total</span>
                  <span className="account-orders-drawer__footer-total-amt">{totalLabel}</span>
                </div>
              </section>
            ) : null}

            <div className="account-orders-drawer__order-meta">
              <div className="account-orders-drawer__footer-pay">
                <div className="account-orders-drawer__footer-pay-pill" aria-label="Order type">
                  <div className="account-orders-drawer__footer-pay-stack">
                    <span className="account-orders-drawer__footer-pay-pill-label">Order type</span>
                    <span className="account-orders-drawer__footer-pay-pill-val">
                      {orderTypeLabel || '—'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="account-orders-drawer__footer-pay">
                <div className="account-orders-drawer__footer-pay-pill" aria-label="Payment method">
                  <div className="account-orders-drawer__footer-pay-stack">
                    <span className="account-orders-drawer__footer-pay-pill-label">Paid via</span>
                    <span className="account-orders-drawer__footer-pay-pill-val">
                      {paymentMethod || '—'}
                    </span>
                  </div>
                  {razorpayPaymentId ? (
                    <>
                      <div className="account-orders-drawer__footer-pay-pill-divider" aria-hidden />
                      <div className="account-orders-drawer__footer-pay-stack">
                        <span className="account-orders-drawer__footer-pay-pill-label">Reference</span>
                        <span className="account-orders-drawer__footer-pay-pill-ref">{razorpayPaymentId}</span>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {showCancel ? (
            <footer className="account-orders-drawer__footer">
              <div className="account-orders-drawer__footer-actions">
                <button
                  type="button"
                  className="account-orders-drawer__help-btn account-orders-drawer__help-btn--cancel"
                  onClick={() => onCancelOrder?.(order)}
                  disabled={cancelling}
                >
                  <FaTimes aria-hidden />
                  {cancelling ? 'Cancelling…' : 'Cancel Order'}
                </button>
              </div>
            </footer>
          ) : null}
        </aside>
      </div>
    </>
  );
}
